import { useState } from "react";
import { ArrowDown, ArrowUp, Check, Pencil, Plus, Trash2, X } from "lucide-react";

/**
 * RewardOptionsEditor
 * Props:
 *   value: string[]
 *   onChange: (newValue: string[]) => void
 *
 * Renders an editable list with add / inline-edit / delete / reorder controls.
 * Used by Admin → Cohort Create / Edit dialogs.
 */
export default function RewardOptionsEditor({ value = [], onChange }) {
  const [editingIdx, setEditingIdx] = useState(-1);
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);
  const [newDraft, setNewDraft] = useState("");

  const items = Array.isArray(value) ? value : [];

  const update = (next) => onChange(next);

  const startEdit = (i) => {
    setEditingIdx(i);
    setDraft(items[i]);
  };
  const commitEdit = () => {
    const v = draft.trim();
    if (!v) return;
    const next = items.slice();
    next[editingIdx] = v;
    update(next);
    setEditingIdx(-1);
    setDraft("");
  };
  const cancelEdit = () => {
    setEditingIdx(-1);
    setDraft("");
  };
  const remove = (i) => update(items.filter((_, idx) => idx !== i));
  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j], next[i]];
    update(next);
  };
  const addNew = () => {
    const v = newDraft.trim();
    if (!v) return;
    update([...items, v]);
    setNewDraft("");
    setAdding(false);
  };

  return (
    <div className="space-y-2" data-testid="reward-options-editor">
      <ul className="space-y-2">
        {items.map((opt, i) => (
          <li
            key={`${opt}-${i}`}
            data-testid={`reward-row-${i}`}
            className="flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900/70 px-2 py-1.5"
          >
            <span className="w-6 shrink-0 text-center text-xs font-mono text-zinc-500">{i + 1}</span>
            {editingIdx === i ? (
              <>
                <input
                  autoFocus
                  data-testid={`reward-edit-input-${i}`}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitEdit();
                    if (e.key === "Escape") cancelEdit();
                  }}
                  className="flex-1 rounded border border-zinc-700 bg-zinc-950 px-2 py-1 text-sm text-white outline-none focus:border-[#A855F7]"
                />
                <button onClick={commitEdit} data-testid={`reward-save-edit-${i}`} className="rounded p-1 text-[#A855F7] hover:bg-[#A855F7]/10" title="Save"><Check className="h-4 w-4" /></button>
                <button onClick={cancelEdit} data-testid={`reward-cancel-edit-${i}`} className="rounded p-1 text-zinc-500 hover:bg-zinc-800" title="Cancel"><X className="h-4 w-4" /></button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm text-zinc-200">{opt}</span>
                <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`reward-up-${i}`} className="rounded p-1 text-zinc-500 hover:bg-zinc-800 disabled:opacity-30" title="Move up"><ArrowUp className="h-4 w-4" /></button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} data-testid={`reward-down-${i}`} className="rounded p-1 text-zinc-500 hover:bg-zinc-800 disabled:opacity-30" title="Move down"><ArrowDown className="h-4 w-4" /></button>
                <button onClick={() => startEdit(i)} data-testid={`reward-edit-${i}`} className="rounded p-1 text-zinc-300 hover:bg-zinc-800" title="Edit"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(i)} data-testid={`reward-delete-${i}`} className="rounded p-1 text-rose-400 hover:bg-rose-500/10" title="Delete"><Trash2 className="h-4 w-4" /></button>
              </>
            )}
          </li>
        ))}
      </ul>

      {items.length === 0 && !adding && (
        <p className="rounded-md border border-dashed border-zinc-800 px-3 py-3 text-center text-xs text-zinc-500">
          No reward options yet. Add the first one below.
        </p>
      )}

      {adding ? (
        <div className="flex items-center gap-2 rounded-md border border-[#A855F7]/40 bg-zinc-900/70 px-2 py-1.5">
          <span className="w-6 shrink-0 text-center text-xs font-mono text-zinc-500">+</span>
          <input
            autoFocus
            data-testid="reward-new-input"
            value={newDraft}
            onChange={(e) => setNewDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") addNew();
              if (e.key === "Escape") { setAdding(false); setNewDraft(""); }
            }}
            placeholder="e.g. AWS credits"
            className="flex-1 rounded border border-zinc-700 bg-zinc-950 px-2 py-1 text-sm text-white outline-none focus:border-[#A855F7]"
          />
          <button onClick={addNew} data-testid="reward-confirm-add" className="rounded p-1 text-[#A855F7] hover:bg-[#A855F7]/10"><Check className="h-4 w-4" /></button>
          <button onClick={() => { setAdding(false); setNewDraft(""); }} data-testid="reward-cancel-add" className="rounded p-1 text-zinc-500 hover:bg-zinc-800"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          data-testid="reward-add-btn"
          className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-zinc-200 hover:border-[#A855F7]/60 hover:text-white"
        >
          <Plus className="h-3.5 w-3.5" /> Add reward
        </button>
      )}

      <p className="text-xs text-zinc-500">
        💡 Tip: keep this list <span className="font-bold text-zinc-300">realistic &amp; deliverable</span> — these are the actual rewards you&apos;ll commit to. You can always update before announcing winners.
      </p>
    </div>
  );
}
